import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Minus } from "lucide-react";
import { Message } from "../../types/conversations";
import { MessagesList } from "../conversations/MessagesList";
import { MessageInput } from "../conversations/MessageInput";
import supportService, {
  SupportMessage,
  SupportRoom,
} from "../../services/support";
import conversationsService from "../../services/conversations";
import { toast } from "react-toastify";

const FloatingChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rooms, setRooms] = useState<SupportRoom[]>([]);
  const [selectedRoomUserId, setSelectedRoomUserId] = useState<number | null>(
    null,
  );
  const [isSupportUser, setIsSupportUser] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Verificar se é usuário de suporte (ID 1)
  useEffect(() => {
    const userId = conversationsService.getCurrentUserId();
    setIsSupportUser(userId === 1);
  }, []);

  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(globalThis.innerWidth < 768);
    };

    checkMobile();
    globalThis.addEventListener("resize", checkMobile);

    return () => globalThis.removeEventListener("resize", checkMobile);
  }, []);

  // Converter SupportMessage para Message (formato do componente)
  const convertToMessage = useCallback((msg: SupportMessage): Message => {
    return {
      id: msg.id,
      chatId: 999, // ID fictício para o chat de suporte
      senderId: msg.sender_id,
      content: msg.content,
      type: msg.type,
      fileUrl: msg.file_url,
      fileName: msg.file_name,
      timestamp: new Date(msg.timestamp),
      status: msg.status,
    };
  }, []);

  const loadRooms = useCallback(async () => {
    try {
      const response = await supportService.listSupportRooms();
      setRooms(response.rooms);

      // Selecionar primeira sala automaticamente se houver
      if (response.rooms.length > 0 && !selectedRoomUserId) {
        setSelectedRoomUserId(response.rooms[0].user_id);
      }
    } catch (error) {
      console.error("Erro ao carregar salas:", error);
      toast.error("Erro ao carregar salas");
    }
  }, [selectedRoomUserId]);

  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    if (isMinimized) return;
    try {
      // Se for suporte e tiver sala selecionada, filtrar por sala
      const roomUserId =
        isSupportUser && selectedRoomUserId ? selectedRoomUserId : undefined;
      const response = await supportService.listSupportMessages(roomUserId);
      const convertedMessages = response.messages.map(convertToMessage);
      setMessages(convertedMessages);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
      toast.error("Erro ao carregar mensagens");
    } finally {
      setIsLoading(false);
    }
  }, [isSupportUser, selectedRoomUserId, convertToMessage, isMinimized]);

  // Carregar salas se for usuário de suporte
  useEffect(() => {
    if (isOpen && isSupportUser) {
      loadRooms();
    }
  }, [isOpen, isSupportUser, loadRooms]);

  // Carregar mensagens ao abrir o chat ou trocar de sala
  useEffect(() => {
    if (isOpen) {
      loadMessages();
    }
  }, [isOpen, loadMessages]);

  // Polling: Verificar novas mensagens a cada 1 minuto quando não minimizado
  useEffect(() => {
    if (!isOpen || isMinimized) return;

    const intervalId = setInterval(() => {
      loadMessages();
    }, 30000); // 30 segundos

    return () => clearInterval(intervalId);
  }, [isOpen, isMinimized, loadMessages]);

  const handleRoomChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = Number.parseInt(event.target.value, 10);
    setSelectedRoomUserId(userId);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, isOpen, isMinimized]);

  const handleSendMessage = async () => {
    // Validar se tem conteúdo OU arquivo
    if (inputValue.trim() === "" && attachedFiles.length === 0) return;

    // Validar se é suporte e tem sala selecionada
    if (isSupportUser && !selectedRoomUserId) {
      toast.error("Selecione uma sala primeiro");
      return;
    }

    try {
      const isImage =
        attachedFiles.length > 0 && attachedFiles[0].type.startsWith("image/");

      let fileUrl: string | undefined;
      let fileName: string | undefined;

      // Se houver imagem, fazer upload primeiro
      if (isImage) {
        toast.info("Enviando imagem...");
        const uploadResult = await supportService.uploadSupportFile(
          attachedFiles[0],
        );

        if (!uploadResult) {
          toast.error("Erro ao fazer upload da imagem");
          return;
        }

        fileUrl = uploadResult.url;
        fileName = uploadResult.fileName;
      }

      const messageData: {
        content: string;
        type: "text" | "image";
        file_url?: string;
        file_name?: string;
        recipient_id?: number;
      } = {
        content: inputValue.trim() || "", // Permite conteúdo vazio se houver imagem
        type: isImage ? "image" : "text",
        ...(isImage && {
          file_url: fileUrl,
          file_name: fileName,
        }),
        // Se for suporte, adicionar recipient_id
        ...(isSupportUser &&
          selectedRoomUserId && {
            recipient_id: selectedRoomUserId,
          }),
      };

      const response = await supportService.sendSupportMessage(messageData);
      const newMessage = convertToMessage(response.message);

      setMessages((prev) => [...prev, newMessage]);
      setInputValue("");
      setAttachedFiles([]);

      // Recarregar salas se for suporte (para atualizar última mensagem)
      if (isSupportUser) {
        loadRooms();
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      toast.error("Erro ao enviar mensagem");
    }
  };

  const handleFileSelect = (files: File[]) => {
    setAttachedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getSelectedRoomName = () => {
    if (!selectedRoomUserId || !rooms.length) return "Suporte Easyfit";
    const room = rooms.find((r) => r.user_id === selectedRoomUserId);
    return room ? room.user_name : "Suporte Easyfit";
  };

  if (globalThis.location.pathname !== "/appointments") {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-full p-3 shadow-lg transition-all duration-200 active:scale-95 touch-manipulation md:bottom-6 md:right-6 md:p-4 md:hover:scale-110"
        aria-label="Abrir chat"
      >
        <MessageCircle className="w-6 h-6 md:w-7 md:h-7" />
        {/* <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-semibold md:w-6 md:h-6 md:text-xs">
          {isSupportUser
            ? rooms.reduce((sum, room) => sum + room.unread_count, 0) || ""
            : messages.filter((m) => m.senderId === 1 && m.status !== "read")
                .length || ""}
        </span> */}
      </button>
    );
  }

  return (
    <>
      {/* Backdrop para mobile */}
      <button
        type="button"
        className="fixed inset-0 bg-black/50 z-40 md:hidden cursor-default"
        onClick={() => setIsOpen(false)}
        aria-label="Fechar chat"
        tabIndex={-1}
      />

      {/* Chat container */}
      <div
        className={`fixed left-0 right-0 bottom-0 z-50 bg-white dark:bg-gray-800 flex flex-col overflow-hidden transition-all duration-300 md:inset-auto md:bottom-6 md:right-6 md:rounded-lg md:shadow-2xl md:border md:border-gray-200 md:dark:border-gray-700 ${
          isMinimized ? "md:h-16 md:w-[420px]" : "md:h-[600px] md:w-[420px]"
        }`}
        style={
          isMobile
            ? {
                top: "max(60px, env(safe-area-inset-top))",
                height:
                  "calc(100dvh - max(60px, env(safe-area-inset-top)) - env(safe-area-inset-bottom))",
              }
            : undefined
        }
      >
        {/* Header personalizado */}
        <div className="bg-purple-600 text-white px-3 py-3 flex items-center justify-between border-b border-purple-700 flex-shrink-0 md:px-4 md:py-4">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-purple-500 flex items-center justify-center md:w-10 md:h-10">
                <MessageCircle className="w-[18px] h-[18px] md:w-5 md:h-5" />
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-purple-600 md:w-3 md:h-3"></span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm md:text-base truncate">
                {getSelectedRoomName()}
              </h3>
              <p className="text-[11px] text-purple-100 md:text-xs">Online</p>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="hover:bg-purple-700 active:bg-purple-800 p-1.5 rounded transition-colors md:p-2 hidden md:block touch-manipulation"
              aria-label={isMinimized ? "Maximizar" : "Minimizar"}
            >
              <Minus size={18} />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-purple-700 active:bg-purple-800 p-1.5 rounded transition-colors md:p-2 touch-manipulation"
              aria-label="Fechar chat"
            >
              <X className="w-[18px] h-[18px] md:w-[18px] md:h-[18px]" />
            </button>
          </div>
        </div>

        {/* Select de salas (apenas para suporte) */}
        {!isMinimized && isSupportUser && (
          <>
            {rooms.length > 0 ? (
              <div className="bg-purple-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                <label
                  htmlFor="room-select"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Sala ativa:
                </label>
                <select
                  id="room-select"
                  value={selectedRoomUserId || ""}
                  onChange={handleRoomChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:text-white"
                >
                  {rooms.map((room) => (
                    <option key={room.user_id} value={room.user_id}>
                      {room.user_name}{" "}
                      {room.unread_count > 0 && `(${room.unread_count})`}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="bg-purple-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                <p className="text-xs text-gray-600 dark:text-gray-300 text-center">
                  Nenhuma conversa disponível ainda
                </p>
              </div>
            )}
          </>
        )}

        {/* Messages Area */}
        {!isMinimized && (
          <>
            <div className="flex-1 min-h-0 flex flex-col bg-gray-50 dark:bg-gray-900">
              {isLoading ? (
                // Skeleton Loading
                <div className="flex-1 px-3 py-3 space-y-3 md:px-4 md:py-4 md:space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg p-3 space-y-2 ${
                          i % 2 === 0 ? "bg-purple-100" : "bg-white"
                        } dark:bg-gray-800`}
                      >
                        <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded animate-pulse w-32"></div>
                        <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded animate-pulse w-24"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                // Empty State
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-20 h-20 mb-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <MessageCircle className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {isSupportUser && !selectedRoomUserId
                      ? "Nenhuma sala selecionada"
                      : "Nenhuma mensagem ainda"}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                    {isSupportUser && !selectedRoomUserId
                      ? "Selecione uma sala para começar a conversar"
                      : isSupportUser
                        ? "Seja o primeiro a enviar uma mensagem para este usuário"
                        : "Envie uma mensagem para começar a conversa com nosso suporte"}
                  </p>
                </div>
              ) : (
                <MessagesList
                  messages={messages}
                  isLoadingMore={false}
                  onScroll={() => {}}
                  messagesContainerRef={messagesContainerRef}
                  messagesEndRef={messagesEndRef}
                />
              )}
            </div>

            {/* Input Area */}
            <div className="flex-shrink-0">
              <MessageInput
                value={inputValue}
                attachedFiles={attachedFiles}
                onValueChange={setInputValue}
                onSend={handleSendMessage}
                onFileSelect={handleFileSelect}
                onRemoveAttachment={handleRemoveAttachment}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default FloatingChat;
